import { useSyncExternalStore } from "react";

const KEY = "mp_tz";
const listeners = new Set();

export const detectedTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const COMMON_TIMEZONES = [
  "Australia/Perth", "Australia/Adelaide", "Australia/Brisbane", "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
  "Asia/Singapore", "Asia/Manila", "Asia/Tokyo", "Asia/Kolkata", "Asia/Dubai", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Toronto", "America/Sao_Paulo", "UTC",
];

export function getTimezone() {
  return localStorage.getItem(KEY) || detectedTimezone();
}

export function setTimezone(tz) {
  if (tz) localStorage.setItem(KEY, tz);
  else localStorage.removeItem(KEY);
  listeners.forEach((l) => l());
}

export function useTimezone() {
  const tz = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, getTimezone);
  return { tz, setTz: setTimezone, isManual: !!localStorage.getItem(KEY), detected: detectedTimezone() };
}

const ABBR_LOCALE = (tz) => (tz.startsWith("Australia/") ? "en-AU" : tz === "Europe/London" || tz === "Europe/Dublin" ? "en-GB" : "en-US");

export function tzAbbr(tz, date = new Date()) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  try {
    return new Intl.DateTimeFormat(ABBR_LOCALE(tz), { timeZone: tz, timeZoneName: "short" }).formatToParts(Number.isNaN(d.getTime()) ? new Date() : d).find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

export function fmtEvent(iso, tz, opts = {}) {
  if (!iso) return "Time TBC";
  const d = new Date(iso);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: tz, weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, ...opts,
  }).format(d);
}

export function fmtEventDate(iso, tz) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
}

// Convert a local wall-clock datetime (YYYY-MM-DDTHH:mm) in the given IANA zone to a UTC ISO string.
export function localToUtcIso(local, tz) {
  if (!local) return null;
  const [datePart, timePart] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" }).formatToParts(new Date(guess));
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  const asIfUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
  return new Date(guess - (asIfUtc - guess)).toISOString();
}

export function utcIsoToLocal(iso, tz) {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(iso));
  const g = (t) => parts.find((p) => p.type === t).value;
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
}
